import { useState } from "react"
import { authClient } from "@/lib/authClient"

interface UploadImageParams {
  imageBlob: Blob
  originalFileName: string
  onSuccess: (result: { fullUrl: string }) => void
  onError: (error: Error) => void
}

export function useImageUploader() {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [uploadProgress, setUploadProgress] = useState<number>(0)

  const uploadImage = async ({
    imageBlob,
    originalFileName,
    onSuccess,
    onError,
  }: UploadImageParams) => {
    setIsLoading(true)
    setUploadProgress(0)

    try {
      // Check authentication with Better Auth
      const sessionData = await authClient.getSession()
      if (!sessionData.data?.session) {
        const err = new Error("Authentication required. Please log in.")
        console.error("No active session")
        onError(err)
        setIsLoading(false)
        return
      }

      // Step 1: Request pre-signed URL from our API
      setUploadProgress(5)
      const presignedResponse = await fetch('/api/upload/presigned-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for Better Auth
        body: JSON.stringify({
          filename: originalFileName,
          contentType: imageBlob.type,
          fileSize: imageBlob.size,
        }),
      })

      if (!presignedResponse.ok) {
        const errorData = await presignedResponse.json()
        throw new Error(errorData.error || 'Failed to get upload URL')
      }

      const { presignedUrl, publicUrl, uploadId } = await presignedResponse.json()
      setUploadProgress(10)

      const pathname = new URL(presignedUrl).pathname.substring(1);

      // Step 2: Upload directly to R2 using pre-signed URL
      const uploadRequest = new XMLHttpRequest()
      
      uploadRequest.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentage = Math.round((e.loaded / e.total) * 90) + 10 // 10-90% for upload
          setUploadProgress(percentage)
        }
      })

      uploadRequest.addEventListener('load', async () => {
        if (uploadRequest.status === 200) {
          setUploadProgress(95)

          try {
            // Step 3: Complete upload by notifying our API
            const completeResponse = await fetch('/api/upload/complete', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
              body: JSON.stringify({
                uploadId,
                fileKey: pathname,
              }),
            })

            if (!completeResponse.ok) {
              const errorData = await completeResponse.json()
              throw new Error(errorData.error || 'Failed to complete upload')
            }

            setUploadProgress(100)
            onSuccess({ fullUrl: publicUrl })
          } catch (error) {
            console.error('Upload completion failed:', error)
            onError(error instanceof Error ? error : new Error('Upload completion failed'))
          } finally {
            setIsLoading(false)
          }
        } else {
          const error = new Error(`Upload failed with status: ${uploadRequest.status}`)
          console.error('R2 upload failed:', error)
          onError(error)
          setIsLoading(false)
        }
      })

      uploadRequest.addEventListener('error', () => {
        const error = new Error('Network error during upload')
        console.error('Upload network error:', error)
        onError(error)
        setIsLoading(false)
      })

      uploadRequest.addEventListener('abort', () => {
        const error = new Error('Upload was aborted')
        console.error('Upload aborted:', error)
        onError(error)
        setIsLoading(false)
      })

      // Start the upload to R2
      uploadRequest.open('PUT', presignedUrl)
      uploadRequest.setRequestHeader('Content-Type', imageBlob.type)
      uploadRequest.send(imageBlob)

    } catch (error) {
      console.error('Upload error:', error)
      onError(error instanceof Error ? error : new Error('Upload failed'))
      setIsLoading(false)
    }
  }

  return { uploadImage, isLoading, uploadProgress }
} 